// =====================================================================
// SEO, Helmet wrapper with canonical, OpenGraph, Twitter, and JSON-LD.
// ---------------------------------------------------------------------
//   <SEO
//     title="Pricing"
//     description="..."
//     path="/pricing"
//     image="https://liquidmemory.ai/og/pricing.png"
//     jsonLd={{ "@context": "https://schema.org", "@type": "Product", ... }}
//   />
//
// Defaults:
//   • The canonical URL is always set from VITE_PUBLIC_SITE_URL + path.
//   • A "SoftwareApplication" structured-data block is emitted on
//     every page so search engines have a stable product description
//     regardless of which route is being indexed. Pass a custom
//     `jsonLd` to override (e.g. Article schema on the paper page).
// =====================================================================

import { useEffect } from "react";
import { Helmet } from "react-helmet-async";

const SITE_NAME = "Liquid Memory";
const SITE_URL =
  import.meta.env.VITE_PUBLIC_SITE_URL || "https://liquidmemory.ai";
const TWITTER_HANDLE = import.meta.env.VITE_TWITTER_HANDLE || "@liquidmemoryai";

const DEFAULT_OG = `${SITE_URL}/og-default.png`; // drop a 1200×630 PNG in /public
const DEFAULT_DESC =
  "A drop-in proxy that compresses your prompts on your own machines before they reach OpenAI, Anthropic, or Google. The text it keeps is byte-exact, it matches direct task success on SWE-bench Verified, and it never costs more than calling the model directly.";

// Reusable software-application schema. SearchGoogle and Bing both
// treat this as a strong signal for the rich product result.
const DEFAULT_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: SITE_NAME,
  applicationCategory: "DeveloperApplication",
  operatingSystem: "macOS, Linux, Windows",
  url: SITE_URL,
  description: DEFAULT_DESC,
  offers: {
    "@type": "Offer",
    price: "0",
    priceCurrency: "USD",
    description: "Commission only on the inference spend we provably remove",
  },
  publisher: {
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/og-default.png`,
  },
};

export default function SEO({
  title,
  description = DEFAULT_DESC,
  path = "",
  image = DEFAULT_OG,
  type = "website",
  noindex = false,
  jsonLd,
}) {
  // Tab title is intentionally minimal, "Liquid Memory" on the home
  // page, "<page> · Liquid Memory" on every other route. The longer
  // marketing tagline lives in the description / og:description.
  const fullTitle = title ? `${title} · ${SITE_NAME}` : SITE_NAME;
  const canonical = `${SITE_URL}${path}`;
  const ld = jsonLd ?? DEFAULT_JSON_LD;

  // Belt-and-suspenders title management.
  // ---------------------------------------------------------------
  // react-helmet-async normally writes the <title> tag for us, but
  // in the Vite dev pipeline (and on some browser configurations
  // where the deferred Helmet flush race-conditions against an
  // initial paint) the title stays at whatever the static index.html
  // shipped with. Setting document.title directly via useEffect is
  // a guaranteed second path: the moment any page mounts a <SEO />,
  // the browser tab updates within one tick. Helmet still handles
  // the other meta tags (canonical, description, og:*, twitter:*,
  // JSON-LD) when it's working, and the duplicate write here is a
  // no-op when both paths agree.
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.title = fullTitle;
    }
  }, [fullTitle]);

  return (
    <Helmet>
      {/* Core */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* OpenGraph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={`${SITE_NAME}, ${title ?? "social card"}`} />

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:creator" content={TWITTER_HANDLE} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={`${SITE_NAME}, ${title ?? "social card"}`} />

      {/* JSON-LD structured data */}
      <script type="application/ld+json">{JSON.stringify(ld)}</script>
    </Helmet>
  );
}
